/**
 * Встраивания (фаза 1.7): allow-list доменов и приведение обычной ссылки к
 * embed-адресу. Используется и в редакторе (превью в ноде), и в читалке
 * (гидрация <div data-embed> → <iframe>). iframe в HTML статьи НЕ хранится —
 * только URL; iframe создаётся здесь после повторной проверки домена.
 */
export interface EmbedTarget {
  src: string;
  /** Высота по умолчанию, px. */
  height: number;
  kind: 'video' | 'doc' | 'map' | 'board';
}

export function toEmbedTarget(raw: string): EmbedTarget | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:') return null;
  const host = u.hostname.replace(/^www\./, '');
  const path = u.pathname;

  // YouTube: watch?v=ID, youtu.be/ID, /shorts/ID, /embed/ID
  if (host === 'youtube.com' || host === 'youtu.be' || host === 'youtube-nocookie.com') {
    const id =
      host === 'youtu.be'
        ? path.slice(1).split('/')[0]
        : u.searchParams.get('v') || path.match(/\/(?:embed|shorts)\/([^/?]+)/)?.[1] || '';
    if (!/^[\w-]{6,}$/.test(id)) return null;
    return { src: `https://www.youtube-nocookie.com/embed/${id}`, height: 400, kind: 'video' };
  }
  if (host === 'rutube.ru') {
    const id = path.match(/\/(?:video|play\/embed)\/([0-9a-f]{32})/)?.[1];
    if (!id) return null;
    return { src: `https://rutube.ru/play/embed/${id}`, height: 400, kind: 'video' };
  }
  if (host === 'vk.com' || host === 'vkvideo.ru') {
    if (path.startsWith('/video_ext.php')) return { src: u.toString(), height: 400, kind: 'video' };
    const m = path.match(/\/video(-?\d+)_(\d+)/);
    if (m) return { src: `https://vk.com/video_ext.php?oid=${m[1]}&id=${m[2]}&hd=2`, height: 400, kind: 'video' };
    return null;
  }
  if (host === 'kinescope.io') {
    const id = path.match(/\/(?:embed\/)?([\w-]{6,})/)?.[1];
    return id ? { src: `https://kinescope.io/embed/${id}`, height: 400, kind: 'video' } : null;
  }
  if (host === 'docs.google.com') {
    // /spreadsheets/d/ID, /document/d/ID, /presentation/d/ID, /forms/d/e/ID
    const m = path.match(/^\/(spreadsheets|document|presentation|forms)\/d\/(e\/)?([\w-]+)/);
    if (!m) return null;
    const kind = m[1];
    const id = m[3];
    const gid = u.searchParams.get('gid');
    const src =
      kind === 'forms'
        ? `https://docs.google.com/forms/d/e/${id}/viewform?embedded=true`
        : kind === 'presentation'
          ? `https://docs.google.com/presentation/d/${id}/embed`
          : `https://docs.google.com/${kind}/d/${id}/preview${gid ? `?gid=${gid}` : ''}`;
    return { src, height: kind === 'spreadsheets' ? 520 : 640, kind: 'doc' };
  }
  if (host === 'drive.google.com') {
    const id = path.match(/\/file\/d\/([\w-]+)/)?.[1];
    return id ? { src: `https://drive.google.com/file/d/${id}/preview`, height: 520, kind: 'doc' } : null;
  }
  if (host === 'figma.com') {
    return { src: `https://www.figma.com/embed?embed_host=brasol&url=${encodeURIComponent(u.toString())}`, height: 520, kind: 'board' };
  }
  if (host === 'miro.com') {
    const id = path.match(/\/(?:app\/)?(?:board|live-embed)\/([\w=]+)/)?.[1];
    return id ? { src: `https://miro.com/app/live-embed/${id}/`, height: 520, kind: 'board' } : null;
  }
  if (host === 'yandex.ru' && (path.startsWith('/map-widget') || path.startsWith('/maps'))) {
    return { src: u.toString(), height: 420, kind: 'map' };
  }
  return null;
}

/**
 * Google-таблица отдельной строкой в статье → живой просмотр в iframe
 * (ArticlePage). Аудит 19.10.2026: раньше хватало подстроки
 * «docs.google.com/spreadsheets» в href, а «опубликованная» ссылка уходила в
 * iframe как есть — `javascript:…//docs.google.com/spreadsheets/…?output=`
 * выполнялся в вики. Теперь адрес разбирается как URL: только https,
 * хост ровно docs.google.com, путь /spreadsheets/….
 * src — адрес для iframe, openHref — для ссылки «В Google ↗».
 */
export function googleSheetEmbed(href: string): { src: string; openHref: string } | null {
  let u: URL;
  try {
    u = new URL(href.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' || u.hostname !== 'docs.google.com') return null;
  if (u.username || u.password || (u.port && u.port !== '443')) return null;
  if (!u.pathname.startsWith('/spreadsheets/')) return null;

  // «Опубликованную в интернете» ссылку (pubhtml / .../pub?...) используем как
  // есть — она показывает РОВНО один выбранный лист, без таб-бара.
  const published = /\/pub(html)?$/.test(u.pathname) || u.searchParams.has('output');
  if (published) {
    const p = new URL(u.href);
    p.hash = '';
    p.searchParams.set('widget', 'true');
    p.searchParams.set('headers', 'false');
    return { src: p.href, openHref: u.href };
  }
  // Обычную ссылку → /preview?gid=<лист> (открывает нужную вкладку).
  const id = /^\/spreadsheets\/d\/([\w-]+)/.exec(u.pathname)?.[1];
  if (!id || id === 'e') return null;
  const gid = u.searchParams.get('gid') ?? /(?:^#|&)gid=(\d+)/.exec(u.hash)?.[1] ?? null;
  const gidPart = gid && /^\d+$/.test(gid) ? `?gid=${gid}` : '';
  return { src: `https://docs.google.com/spreadsheets/d/${id}/preview${gidPart}`, openHref: u.href };
}

/** Домены, которые понимает toEmbedTarget — для подсказки в редакторе. */
export const EMBED_HINT = 'YouTube, RuTube, VK Видео, Kinescope, Google Docs/Sheets/Slides/Forms, Google Drive, Figma, Miro, Яндекс.Карты';
